import React, { useState, useEffect } from "react";
import { Box, Button, Typography, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, IconButton, Table, TableHead, TableBody, TableRow, TableCell, useTheme, useMediaQuery, Fade, Alert, Chip
 } from "@mui/material";
import { startShift, endShift, handoverShift, executeCheckActiveDailyshift, getShiftTitles, getActiveShiftTransactions, getActiveShiftData } from "../../../services/Settings/ServicesandTaxSettingsAndSessios/SessoinAndShiftsApi";
import { fetchBanks } from "../../../services/pos/Tables";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { getUserShiftReport } from "../../../services/Reports/ReportsApi";
import PrintIcon from "@mui/icons-material/Print";
import WorkIcon from "@mui/icons-material/Work";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

const ShiftActions = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  
  // ✅ كشف دقيق لجميع أحجام التابلت
  const isSmallTablet = useMediaQuery("(min-width: 600px) and (max-width: 768px)");
  const isMediumTablet = useMediaQuery("(min-width: 769px) and (max-width: 900px)");
  const isLargeTablet = useMediaQuery("(min-width: 901px) and (max-width: 1024px)");
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 1024px)");
  
  // ✅ كشف الوضع الأفقي/العمودي
  const isPortrait = useMediaQuery("(orientation: portrait)");
  const isLandscape = useMediaQuery("(orientation: landscape)");

  // ✅ حساب أحجام العناصر
  const getBoxPadding = () => {
    if (isSmallTablet) return 2;
    if (isMediumTablet) return 2.5;
    return 3;
  };

  const getBoxGap = () => {
    if (isSmallTablet) return 1.5;
    if (isMediumTablet) return 2;
    return 2.5;
  };

  const getTitleSize = () => {
    if (isSmallTablet) return "16px";
    if (isMediumTablet) return "18px";
    return "20px";
  };

  const getBodySize = () => {
    if (isSmallTablet) return "12px";
    if (isMediumTablet) return "13px";
    return "14px";
  };

  const getButtonSize = () => {
    if (isSmallTablet) return "small";
    return "medium";
  };

  const getButtonFontSize = () => {
    if (isSmallTablet) return "13px";
    if (isMediumTablet) return "14px";
    return "15px";
  };

  const getIconSize = () => {
    if (isSmallTablet) return 20;
    if (isMediumTablet) return 22;
    return 24;
  };

  const getDialogMaxWidth = () => {
    if (isSmallTablet) return isPortrait ? "xs" : "sm";
    if (isMediumTablet) return "sm";
    return "sm";
  };

  const getTableFontSize = () => {
    if (isSmallTablet) return "11px";
    if (isMediumTablet) return "12px";
    return "13px";
  };

  const [loading, setLoading] = useState(false);
  const [shiftActive, setShiftActive] = useState(() => {
    return localStorage.getItem("shift_started") === "true";
  });
  const [sessionStatus, setSessionStatus] = useState(null);
  const [banks, setBanks] = useState([]);
  const [error, setError] = useState("");
  const [handoverModalOpen, setHandoverModalOpen] = useState(false);
  const [startShiftModalOpen, setStartShiftModalOpen] = useState(false);
  const [shiftData, setShiftData] = useState({
    shiftId: "",
    rootId: localStorage.getItem("branch_id") || "",
  });
  const [handoverData, setHandoverData] = useState({
    from_bank_id: "",
    to_bank_id: "",
    amount: "100",
  });
  const [shifts, setShifts] = useState([]);
  const [activeShiftCashTotal, setActiveShiftCashTotal] = useState(null);
  const [showTransactionsDialog, setShowTransactionsDialog] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [shiftTransactions, setShiftTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [activeShiftId, setActiveShiftId] = useState("");
  const [printConfirmOpen, setPrintConfirmOpen] = useState(false);
  const [printUserName, setPrintUserName] = useState("");
  const [printMode, setPrintMode] = useState(false);
  const [endConfirmOpen, setEndConfirmOpen] = useState(false);
  const [printBeforeEndOpen, setPrintBeforeEndOpen] = useState(false);
  const [printTransactions, setPrintTransactions] = useState([]);

  useEffect(() => {
    // ✅ جلب حالة الشيفت من الـ API فقط
    const fetchShiftStatus = async () => {
      try {
        const status = await executeCheckActiveDailyshift();
        setShiftActive(status === "active");
        setSessionStatus(status);
      } catch (error) {
        setSessionStatus("تعذر جلب حالة الشيفت");
      }
    };

    const fetchBanksList = async () => {
      try {
        const data = await fetchBanks();
        setBanks(data);
      } catch (err) {
        setError("❌ فشل في جلب الخزن.");
      }
    };

    const fetchShiftTitles = async () => {
      try {
        const rootId = localStorage.getItem("branch_id");
        if (!rootId) throw new Error("❌ لم يتم العثور على branch_id.");
        const titles = await getShiftTitles(rootId);
        setShifts(titles);
      } catch (err) {
        console.error("❌ فشل في جلب أسماء الشيفتات:", err.message);
      }
    };

    const fetchActiveShiftCash = async () => {
      try {
        // جلب sessionId من localStorage أو منطقك الخاص
        const sessionId = localStorage.getItem("active_session_id");
        if (sessionId) {
          const txs = await getActiveShiftTransactions(sessionId);
          setActiveShiftCashTotal(txs.total_invoice_amount || 0);
        }
      } catch (err) {
        setActiveShiftCashTotal(null);
      }
    };

    const fetchActiveShiftId = async () => {
      try {
        const activeShifts = await getActiveShiftData();
        // توقع أن الدالة ترجع مصفوفة، خذ أول شيفت نشط
        if (Array.isArray(activeShifts) && activeShifts.length > 0) {
          setActiveShiftId(activeShifts[0].id);
        } else {
          setActiveShiftId("");
        }
      } catch (err) {
        setActiveShiftId("");
      }
    };

    fetchShiftStatus();
    fetchBanksList();
    fetchShiftTitles();
    fetchActiveShiftCash();
    fetchActiveShiftId();
  }, []);

  const handleStartShift = async () => {
    setLoading(true);
    try {
      await startShift(shiftData.shiftId, shiftData.rootId);
      localStorage.setItem("active_shift_id", shiftData.shiftId);
      alert("تم بدء الشيفت بنجاح.");
      setShiftActive(true);
      localStorage.setItem("shift_started", "true");
      setStartShiftModalOpen(false);
    } catch (error) {
      alert("فشل في بدء الشيفت.");
    } finally {
      setLoading(false);
    }
  };

  const handleEndShift = async () => {
    // قبل السؤال عن إنهاء الشيفت، اسأل أولاً عن الطباعة
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    setPrintUserName(user?.username || "");
    setPrintBeforeEndOpen(true);
  };

  const handlePrintBeforeEnd = (shouldPrint) => {
    setPrintBeforeEndOpen(false);
    if (shouldPrint) {
      handlePrintTransactions();
    }
    setEndConfirmOpen(true); // بعد الطباعة أو تخطيها، اسأل عن إنهاء الشيفت
  };

  const confirmEndShift = async () => {
    setLoading(true);
    try {
      await endShift();
      alert("تم إنهاء الشيفت بنجاح.");
      setShiftActive(false);
      localStorage.setItem("shift_started", "false");
    } catch (error) {
      alert("فشل في إنهاء الشيفت.");
    } finally {
      setLoading(false);
      setEndConfirmOpen(false);
    }
  };

  const handleHandoverShift = async () => {
    setHandoverModalOpen(true); // Open the modal
  };

  const handleHandoverSubmit = async () => {
    setLoading(true);
    try {
      await handoverShift(handoverData);
      alert("تم قفل الشيفت وترحيل النقدية بنجاح.");
      setShiftActive(false);
      localStorage.setItem("shift_started", "false");
      setHandoverModalOpen(false);
      askPrintReport();
    } catch (error) {
      alert("فشل في قفل الشيفت وترحيل النقدية.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setShiftData((prev) => ({ ...prev, [name]: value }));
  };

  const handleHandoverChange = (e) => {
    const { name, value } = e.target;
    setHandoverData((prev) => ({ ...prev, [name]: value }));
  };

  const handleShowTransactions = async () => {
    const userId = localStorage.getItem("user_id");
    const shiftId = activeShiftId;

    if (!userId || !shiftId) {
      setShiftTransactions([]);
      setShowTransactionsDialog(true);
      return;
    }

    setShowTransactionsDialog(true);
    setTransactionsLoading(true);
    try {
      const result = await getUserShiftReport(userId, shiftId);
      // ✅ المعاملات موجودة تحت result.transactions مباشرة
      setShiftTransactions(Array.isArray(result?.transactions) ? result.transactions : []);
    } catch (err) {
      console.error("❌ خطأ في جلب معاملات الشيفت:", err.message);
      setShiftTransactions([]);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const handlePrintTransactions = async () => {
    // جلب بيانات المعاملات من getUserShiftReport قبل الطباعة
    const userId = localStorage.getItem("user_id");
    const shiftId = activeShiftId;
    let transactions = [];
    if (userId && shiftId) {
      try {
        const result = await getUserShiftReport(userId, shiftId);
        transactions = Array.isArray(result?.transactions) ? result.transactions : [];
      } catch (err) {
        transactions = [];
      }
    }
    setPrintTransactions(transactions);
    setPrintMode(true);
    setTimeout(() => {
      window.print();
      setPrintMode(false);
    }, 100);
  };

  const askPrintReport = () => {
    // جلب اسم المستخدم من اللوكال ستورج
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    setPrintUserName(user?.username || "");
    setPrintConfirmOpen(true);
  };

  // ✅ إذا لم يكن تابلت، لا تعرض المكون
  if (!isTablet) {
    return null;
  }

  return (
    <Fade in={true} timeout={300}>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={getBoxGap()}
        sx={{
          p: getBoxPadding(),
          borderRadius: isSmallTablet ? 2 : 3,
          boxShadow: isDark
            ? "0 8px 24px rgba(0,0,0,0.4)"
            : "0 8px 24px rgba(0,0,0,0.08)",
          background: isDark
            ? "linear-gradient(135deg, #1c1c2b 0%, #26334d 100%)"
            : "linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)",
          color: isDark ? "#fff" : "#232334",
          border: isDark ? "1px solid #333" : "1px solid #eee",
          transition: "all 0.3s ease",
          minHeight: isSmallTablet ? 300 : 350,
          maxWidth: "100%",
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <WorkIcon
            sx={{
              fontSize: getIconSize(),
              color: isDark ? "#f48fb1" : "#1976d2",
              animation: shiftActive ? "pulse 1.5s infinite" : "none",
              "@keyframes pulse": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.6 },
              },
            }}
          />
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              fontSize: getTitleSize(),
              color: isDark ? "#f48fb1" : "#1976d2",
            }}
          >
            إدارة الشيفتات
          </Typography>
        </Box>

        {/* Status Badge */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: isSmallTablet ? 1.5 : 2,
            py: isSmallTablet ? 0.8 : 1,
            borderRadius: isSmallTablet ? 1.5 : 2,
            bgcolor: shiftActive
              ? isDark
                ? "rgba(76, 175, 80, 0.15)"
                : "rgba(76, 175, 80, 0.08)"
              : isDark
              ? "rgba(255, 152, 0, 0.15)"
              : "rgba(255, 152, 0, 0.08)",
            border: `1px solid ${
              shiftActive
                ? isDark
                  ? "rgba(76, 175, 80, 0.3)"
                  : "rgba(76, 175, 80, 0.2)"
                : isDark
                ? "rgba(255, 152, 0, 0.3)"
                : "rgba(255, 152, 0, 0.2)"
            }`,
            width: "100%",
            justifyContent: "center",
          }}
        >
          {shiftActive ? (
            <>
              <CheckCircleIcon sx={{ color: "#4caf50", fontSize: getIconSize() - 4 }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#4caf50", fontSize: getBodySize() }}
              >
                الشيفت مفعّل
              </Typography>
            </>
          ) : (
            <>
              <ErrorIcon sx={{ color: "#ff9800", fontSize: getIconSize() - 4 }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#ff9800", fontSize: getBodySize() }}
              >
                الشيفت معطّل
              </Typography>
            </>
          )}
        </Box>

        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ fontSize: getBodySize() }}
        >
          حالة اليومية: {sessionStatus || "جاري التحميل..."}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: "100%", fontSize: getBodySize() }}>
            {error}
          </Alert>
        )}

        {/* Banks List */}
        {banks.length > 0 && (
          <Box sx={{ width: "100%", textAlign: "center" }}>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: getBodySize(), mb: 1 }}>
              الخزن المتاحة:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, justifyContent: "center" }}>
              {banks.slice(0, isSmallTablet ? 3 : 5).map((bank) => (
                <Chip
                  key={bank.id}
                  label={bank.name}
                  size="small"
                  sx={{ fontSize: getBodySize() - 1 }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Cash Total */}
        {activeShiftCashTotal !== null && (
          <Typography
            variant="h6"
            sx={{
              color: "#43a047",
              fontWeight: 900,
              fontSize: isSmallTablet ? 16 : isMediumTablet ? 18 : 20,
              letterSpacing: 0.5,
              textAlign: "center",
            }}
          >
            إجمالي النقدية: {activeShiftCashTotal.toLocaleString()} ج.م
          </Typography>
        )}

        {/* Action Buttons */}
        {loading ? (
          <CircularProgress size={isSmallTablet ? 36 : 40} />
        ) : shiftActive ? (
          <Box display="flex" flexDirection="column" gap={1.5} width="100%">
            <Button
              variant="contained"
              color="secondary"
              onClick={handleEndShift}
              fullWidth
              size={getButtonSize()}
              sx={{
                fontWeight: 600,
                fontSize: getButtonFontSize(),
                py: getButtonSize() === "small" ? 1 : 1.2,
                borderRadius: isSmallTablet ? 1.5 : 2,
                transition: "all 0.25s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                },
              }}
            >
              قفل الشيفت
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => setHandoverModalOpen(true)}
              fullWidth
              size={getButtonSize()}
              sx={{
                fontWeight: 600,
                fontSize: getButtonFontSize(),
                py: getButtonSize() === "small" ? 1 : 1.2,
                borderRadius: isSmallTablet ? 1.5 : 2,
                transition: "all 0.25s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(244, 67, 54, 0.3)",
                },
              }}
            >
              قفل الشيفت وترحيل النقدية
            </Button>
          </Box>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setStartShiftModalOpen(true)}
            fullWidth
            size={getButtonSize()}
            sx={{
              fontWeight: 600,
              fontSize: getButtonFontSize(),
              py: getButtonSize() === "small" ? 1 : 1.2,
              borderRadius: isSmallTablet ? 1.5 : 2,
              transition: "all 0.25s",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
              },
            }}
          >
            بداية الشيفت
          </Button>
        )}

        {/* Current Shift Info */}
        {shiftActive && (
          <Box
            sx={{
              display: "flex",
              flexDirection: isSmallTablet ? "column" : "row",
              alignItems: "center",
              gap: isSmallTablet ? 1 : 2,
              width: "100%",
              justifyContent: "center",
              p: 1.5,
              bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
              borderRadius: isSmallTablet ? 1.5 : 2,
            }}
          >
            <Typography variant="body1" fontWeight="bold" sx={{ fontSize: getBodySize() }}>
              اسم الشيفت: {
                (() => {
                  const activeShift = shifts.find(s => String(s.id) === String(activeShiftId));
                  return activeShift?.title || "غير محدد";
                })()
              }
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton
                color="primary"
                onClick={handleShowTransactions}
                disabled={!shiftActive || !activeShiftId}
                size="small"
                sx={{
                  transition: "all 0.2s",
                  "&:hover": {
                    transform: "scale(1.1)",
                  },
                }}
              >
                <ReceiptLongIcon fontSize={isSmallTablet ? "small" : "medium"} />
              </IconButton>
              <Typography variant="caption" sx={{ fontSize: getBodySize() - 2 }}>
                عرض معاملات الشيفت
              </Typography>
            </Box>
          </Box>
        )}

        {/* Start Shift Modal */}
        <Dialog
          open={startShiftModalOpen}
          onClose={() => setStartShiftModalOpen(false)}
          fullWidth
          maxWidth={getDialogMaxWidth()}
        >
          <DialogTitle sx={{ fontSize: getTitleSize() }}>بداية الشيفت</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
              <TextField
                select
                label="اختر الشيفت"
                name="shiftId"
                value={shiftData.shiftId}
                onChange={handleInputChange}
                fullWidth
                size={getButtonSize()}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: getBodySize(),
                  },
                }}
              >
                {shifts.map((shift) => (
                  <MenuItem key={shift.id} value={shift.id} sx={{ fontSize: getBodySize() }}>
                    {shift.title || "بدون اسم"}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setStartShiftModalOpen(false)}
              variant="outlined"
              size={getButtonSize()}
              sx={{ fontSize: getButtonFontSize() }}
            >
              إلغاء
            </Button>
            <Button
              onClick={handleStartShift}
              variant="contained"
              color="primary"
              size={getButtonSize()}
              sx={{ fontSize: getButtonFontSize() }}
            >
              بدء الشيفت
            </Button>
          </DialogActions>
        </Dialog>

        {/* Handover Modal */}
        <Dialog
          open={handoverModalOpen}
          onClose={() => setHandoverModalOpen(false)}
          fullWidth
          maxWidth={getDialogMaxWidth()}
        >
          <DialogTitle sx={{ fontSize: getTitleSize() }}>ترحيل النقدية</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
              <TextField
                select
                label="من الخزنة"
                name="from_bank_id"
                value={handoverData.from_bank_id}
                onChange={(e) => setHandoverData((prev) => ({ ...prev, from_bank_id: e.target.value }))}
                fullWidth
                size={getButtonSize()}
                sx={{ "& .MuiInputBase-input": { fontSize: getBodySize() } }}
              >
                {banks.map((bank) => (
                  <MenuItem key={bank.id} value={bank.id} sx={{ fontSize: getBodySize() }}>
                    {bank.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="إلى الخزنة"
                name="to_bank_id"
                value={handoverData.to_bank_id}
                onChange={(e) => setHandoverData((prev) => ({ ...prev, to_bank_id: e.target.value }))}
                fullWidth
                size={getButtonSize()}
                sx={{ "& .MuiInputBase-input": { fontSize: getBodySize() } }}
              >
                {banks.map((bank) => (
                  <MenuItem key={bank.id} value={bank.id} sx={{ fontSize: getBodySize() }}>
                    {bank.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="المبلغ"
                name="amount"
                type="text"
                value={handoverData.amount}
                onChange={(e) => setHandoverData((prev) => ({ ...prev, amount: e.target.value }))}
                fullWidth
                size={getButtonSize()}
                sx={{ "& .MuiInputBase-input": { fontSize: getBodySize() } }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setHandoverModalOpen(false)}
              variant="outlined"
              size={getButtonSize()}
              sx={{ fontSize: getButtonFontSize() }}
            >
              إلغاء
            </Button>
            <Button
              onClick={handleHandoverSubmit}
              variant="contained"
              color="primary"
              size={getButtonSize()}
              sx={{ fontSize: getButtonFontSize() }}
            >
              ترحيل
            </Button>
          </DialogActions>
        </Dialog>

        {/* Transactions Dialog */}
        <Dialog
          open={showTransactionsDialog}
          onClose={() => setShowTransactionsDialog(false)}
          fullWidth
          maxWidth={isSmallTablet ? "sm" : "md"}
        >
          <DialogTitle sx={{ fontSize: getTitleSize() }}>معاملات الشيفت</DialogTitle>
          <DialogContent>
            {transactionsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={isSmallTablet ? 36 : 40} />
              </Box>
            ) : shiftTransactions.length === 0 ? (
              <Typography color="text.secondary" sx={{ fontSize: getBodySize(), textAlign: "center", py: 3 }}>
                لا توجد معاملات لهذا الشيفت.
              </Typography>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table size={isSmallTablet ? "small" : "medium"}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", fontSize: getTableFontSize() }}>رقم العملية</TableCell>
                      <TableCell sx={{ fontWeight: "bold", fontSize: getTableFontSize() }}>النوع</TableCell>
                      <TableCell sx={{ fontWeight: "bold", fontSize: getTableFontSize() }}>المبلغ</TableCell>
                      <TableCell sx={{ fontWeight: "bold", fontSize: getTableFontSize() }}>الخزنة</TableCell>
                      {!isSmallTablet && (
                        <>
                          <TableCell sx={{ fontWeight: "bold", fontSize: getTableFontSize() }}>العميل</TableCell>
                          <TableCell sx={{ fontWeight: "bold", fontSize: getTableFontSize() }}>نوع الأوردر</TableCell>
                        </>
                      )}
                      <TableCell sx={{ fontWeight: "bold", fontSize: getTableFontSize() }}>التاريخ</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shiftTransactions.map((tx) => (
                      <TableRow key={tx.id}>
                        <TableCell sx={{ fontSize: getTableFontSize() }}>{tx.id}</TableCell>
                        <TableCell sx={{ fontSize: getTableFontSize() }}>{tx.type}</TableCell>
                        <TableCell sx={{ fontSize: getTableFontSize() }}>{tx.amount} ج.م</TableCell>
                        <TableCell sx={{ fontSize: getTableFontSize() }}>{tx.bank?.name || "غير محدد"}</TableCell>
                        {!isSmallTablet && (
                          <>
                            <TableCell sx={{ fontSize: getTableFontSize() }}>{tx.invoice?.customer?.name || "بدون عميل"}</TableCell>
                            <TableCell sx={{ fontSize: getTableFontSize() }}>{tx.invoice?.order?.order_type || "-"}</TableCell>
                          </>
                        )}
                        <TableCell sx={{ fontSize: getTableFontSize() }}>{tx.transaction_date}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setShowTransactionsDialog(false)}
              variant="outlined"
              size={getButtonSize()}
              sx={{ fontSize: getButtonFontSize() }}
            >
              إغلاق
            </Button>
          </DialogActions>
        </Dialog>

        {/* Print Confirm Dialog */}
        <Dialog open={printConfirmOpen} onClose={() => setPrintConfirmOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ textAlign: "center", fontSize: getTitleSize() }}>تأكيد طباعة تقرير الشيفت</DialogTitle>
          <DialogContent sx={{ textAlign: "center" }}>
            <Typography sx={{ fontSize: getBodySize() }}>
              هل تريد طباعة تقرير معاملات الشيفت لهذا المستخدم؟
              <br />
              اسم المستخدم: <b>{printUserName}</b>
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center" }}>
            <Button onClick={() => setPrintConfirmOpen(false)} color="secondary" size={getButtonSize()}>
              لا
            </Button>
            <Button
              onClick={() => {
                setPrintConfirmOpen(false);
                handlePrintTransactions();
              }}
              color="primary"
              variant="contained"
              size={getButtonSize()}
            >
              نعم، اطبع التقرير
            </Button>
          </DialogActions>
        </Dialog>

        {/* Print Before End Dialog */}
        <Dialog open={printBeforeEndOpen} onClose={() => setPrintBeforeEndOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ textAlign: "center", fontSize: getTitleSize() }}>تأكيد طباعة تقرير الشيفت</DialogTitle>
          <DialogContent sx={{ textAlign: "center" }}>
            <Typography sx={{ fontSize: getBodySize() }}>
              هل تريد طباعة تقرير معاملات الشيفت لهذا المستخدم؟
              <br />
              اسم المستخدم: <b>{printUserName}</b>
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center" }}>
            <Button onClick={() => handlePrintBeforeEnd(false)} color="secondary" size={getButtonSize()}>
              لا
            </Button>
            <Button
              onClick={() => handlePrintBeforeEnd(true)}
              color="primary"
              variant="contained"
              size={getButtonSize()}
            >
              نعم، اطبع التقرير
            </Button>
          </DialogActions>
        </Dialog>

        {/* End Confirm Dialog */}
        <Dialog open={endConfirmOpen} onClose={() => setEndConfirmOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ textAlign: "center", fontSize: getTitleSize() }}>تأكيد إنهاء الشيفت</DialogTitle>
          <DialogContent sx={{ textAlign: "center" }}>
            <Typography sx={{ fontSize: getBodySize() }}>
              هل أنت متأكد أنك تريد إنهاء الشيفت الحالي؟
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center" }}>
            <Button onClick={() => setEndConfirmOpen(false)} color="secondary" size={getButtonSize()}>
              إلغاء
            </Button>
            <Button
              onClick={confirmEndShift}
              color="primary"
              variant="contained"
              size={getButtonSize()}
            >
              نعم، إنهاء الشيفت
            </Button>
          </DialogActions>
        </Dialog>

        {/* Print Mode */}
        {printMode && (
          <Box
            id="print-area"
            sx={{
              display: "block",
              position: "absolute",
              left: 0,
              top: 0,
              width: "100vw",
              bgcolor: "#fff",
              color: "#232334",
              zIndex: 9999,
              p: 4,
              fontFamily: "Cairo, Arial, sans-serif",
            }}
          >
            <Typography variant="h4" align="center" sx={{ mb: 2, fontWeight: "bold", color: "#1976d2", fontSize: 36 }}>
              تقرير معاملات الشيفت
            </Typography>
            <Typography variant="h6" align="center" sx={{ mb: 2, fontWeight: "bold", fontSize: 28 }}>
              اسم الشيفت: {selectedShift?.title || "غير محدد"}
            </Typography>
            <Typography align="center" sx={{ mb: 2, fontWeight: "bold", fontSize: 22 }}>
              اسم المستخدم: <b>{printUserName}</b>
            </Typography>
            <Table sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", fontSize: 20 }}>رقم العملية</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: 20 }}>النوع</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: 20 }}>المبلغ</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: 20 }}>الخزنة</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: 20 }}>العميل</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: 20 }}>نوع الأوردر</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: 20 }}>التاريخ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(printTransactions.length > 0 ? printTransactions : shiftTransactions).map((tx) => (
                  <TableRow key={tx.id}>
                    <TableCell sx={{ fontSize: 18 }}>{tx.id}</TableCell>
                    <TableCell sx={{ fontSize: 18 }}>{tx.type}</TableCell>
                    <TableCell sx={{ fontSize: 18 }}>{tx.amount} ج.م</TableCell>
                    <TableCell sx={{ fontSize: 18 }}>{tx.bank?.name || "غير محدد"}</TableCell>
                    <TableCell sx={{ fontSize: 18 }}>{tx.invoice?.customer?.name || "بدون عميل"}</TableCell>
                    <TableCell sx={{ fontSize: 18 }}>{tx.invoice?.order?.order_type || "-"}</TableCell>
                    <TableCell sx={{ fontSize: 18 }}>{tx.transaction_date}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography align="center" sx={{ mt: 3, fontSize: 20, fontWeight: "bold" }}>
              تم الطباعة بتاريخ: {new Date().toLocaleString("ar-EG")}
            </Typography>
          </Box>
        )}

        <style>
          {`
            @media print {
              body * {
                visibility: hidden !important;
              }
              #print-area, #print-area * {
                visibility: visible !important;
              }
              #print-area {
                position: absolute !important;
                left: 0 !important;
                top: 0 !important;
                width: 100vw !important;
                background: #fff !important;
                color: #232334 !important;
                z-index: 99999 !important;
                font-size: 20px !important;
                font-family: Cairo, Arial, sans-serif !important;
              }
              #print-area table, #print-area th, #print-area td {
                font-size: 18px !important;
                font-weight: bold !important;
              }
            }
          `}
        </style>
      </Box>
    </Fade>
  );
};

export default ShiftActions;
